import { useState, useEffect, useSyncExternalStore } from 'react';
import { X, RotateCcw, Cloud, Database, DatabaseZap, Search, Radio, Archive, HardDrive } from 'lucide-react';
import { networkStats } from '@/lib/network-stats';
import type { NetworkStatsSnapshot } from '@/lib/network-stats';

/** Subscribe adapter for useSyncExternalStore */
function subscribeToStats(cb: () => void) {
  return networkStats.subscribeSimple(cb);
}

function getStatsSnapshot(): NetworkStatsSnapshot {
  return networkStats.getSnapshot();
}

/**
 * Live debug network stats overlay — shows CF calls, Firestore reads,
 * RTDB events, and cache hits/misses in real-time.
 *
 * Visibility gated by `localStorage.getItem('devModeViewNetworkInfo') === 'true'`.
 * User can close with the X button; re-enable by setting the localStorage flag again.
 */
export function NetworkStatsOverlay() {
  const [visible, setVisible] = useState(false);

  // Check localStorage on mount and when storage changes
  useEffect(() => {
    const check = () => {
      try {
        setVisible(localStorage.getItem('devModeViewNetworkInfo') === 'true');
      } catch {
        setVisible(false);
      }
    };
    check();

    // Listen for storage changes from other tabs
    window.addEventListener('storage', check);
    return () => window.removeEventListener('storage', check);
  }, []);

  const stats = useSyncExternalStore(subscribeToStats, getStatsSnapshot);

  if (!visible) return null;

  const handleClose = () => {
    setVisible(false);
    try {
      localStorage.removeItem('devModeViewNetworkInfo');
    } catch { /* ignore */ }
  };

  const handleReset = () => {
    networkStats.reset();
  };

  return (
    <div className="fixed bottom-4 right-4 z-[9999] w-56 rounded-xl border border-white/10 bg-black/70 backdrop-blur-md text-white shadow-2xl select-none font-mono text-[11px]">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-white/60">
          Network Stats
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={handleReset}
            className="rounded p-0.5 hover:bg-white/10 transition-colors"
            title="Reset counters"
          >
            <RotateCcw className="h-3 w-3 text-white/50 hover:text-white" />
          </button>
          <button
            onClick={handleClose}
            className="rounded p-0.5 hover:bg-white/10 transition-colors"
            title="Close"
          >
            <X className="h-3 w-3 text-white/50 hover:text-white" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="px-3 py-2 space-y-1.5">
        <StatRow
          icon={<Cloud className="h-3 w-3" />}
          label="CF Calls"
          value={stats.cfCalls}
          color="text-sky-400"
        />
        <StatRow
          icon={<Search className="h-3 w-3" />}
          label="FS Read Calls"
          value={stats.firestoreReadCalls}
          color="text-yellow-400"
        />
        <StatRow
          icon={<Database className="h-3 w-3" />}
          label="FS Docs Read"
          value={stats.firestoreReads}
          color="text-amber-400"
        />
        <StatRow
          icon={<DatabaseZap className="h-3 w-3" />}
          label="Firestore Writes"
          value={stats.firestoreWrites}
          color="text-orange-400"
        />
        <StatRow
          icon={<Radio className="h-3 w-3" />}
          label="RTDB Events"
          value={stats.rtdbEvents}
          color="text-emerald-400"
        />
        <div className="border-t border-white/5 pt-1.5">
          <StatRow
            icon={<Archive className="h-3 w-3" />}
            label="Cache Hit"
            value={stats.cacheHits}
            color="text-green-400"
          />
          <StatRow
            icon={<Archive className="h-3 w-3" />}
            label="Cache Miss"
            value={stats.cacheMisses}
            color="text-red-400"
          />
          <StatRow
            icon={<HardDrive className="h-3 w-3" />}
            label="IDB Hits"
            value={stats.idbCacheHits}
            color="text-cyan-400"
          />
        </div>
      </div>
    </div>
  );
}

function StatRow({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className={`flex items-center gap-1.5 ${color}`}>
        {icon}
        <span className="text-white/70">{label}</span>
      </div>
      <span className={`tabular-nums font-semibold ${color}`}>{value.toLocaleString()}</span>
    </div>
  );
}
